// @ts-nocheck
import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import imgPayku from "../../assets/images/logoPayku.webp";
import imgBinance from "../../assets/images/modal-icons/binance-exchange.svg";
import imgZelle from "../../assets/images/modal-icons/zelle.svg";
import usePagoMovilRegistration from "../../hooks/usePagoMovilRegistration";
import {useBalanceStore} from "../../store/useBalance.store";
import useCurrencyStore from "../../store/useCurrency.store";
import BankOptions from "./BankOptions";
import BinanceOptions from "./BinanceOptions";
import PaykuOptions from "./PaykuOptions";
import ZelleOptions from "./ZelleOptions";

const containerCls = "px-2 rounded-lg";
const instructionsCls = "text-[var(--color-text-secondary)] mb-6 text-sm text-center";
const logoContainerCls =
  "flex justify-center items-center gap-4 my-6 flex-wrap max-md:gap-3 max-[580px]:flex-col max-[580px]:gap-3";

const methodCardStyle = () => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 154,
  height: 88,
  borderRadius: 14,
  background: "transparent",
  border: "none",
  boxShadow: "none",
  cursor: "pointer",
  transition: "box-shadow 0.2s, transform 0.2s, background 0.2s",
  padding: "10px 14px",
  boxSizing: "border-box",
});

const methodCardHoverStyle = {
  background: "rgba(215,50,168,0.07)",
  boxShadow: "inset 0 0 0 1.5px rgba(215,50,168,0.6), 0 0 20px rgba(215,50,168,0.18)",
  transform: "translateY(-2px)",
};

const logoPagoMovilCls = "w-full h-auto object-contain";
const logoZelleCls = "w-full h-auto object-contain";
const logoBinanceCls = "w-full h-auto object-contain";
const logoPaykuCls = "w-full h-auto object-contain";

const MethodCard = ({src, alt, onClick, children}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={hovered ? {...methodCardStyle(), ...methodCardHoverStyle} : methodCardStyle()}
    >
      {src && <img src={src} alt={alt} style={{maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8}} />}
      {children}
    </div>
  );
};

const PagoMovilCard = ({onClick}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={hovered ? {...methodCardStyle(), ...methodCardHoverStyle} : methodCardStyle()}
    >
      <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: 6}}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="5" y="2" width="14" height="20" rx="2" stroke="white" strokeWidth="1.5"/>
          <circle cx="12" cy="18" r="1" fill="white"/>
          <path d="M9 6h6M9 9h4" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M14 12l-2.5 2.5L10 13" stroke="#fa5400" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span style={{color: "white", fontWeight: 700, fontSize: 11, letterSpacing: "0.02em", lineHeight: 1}}>Pago M&oacute;vil</span>
      </div>
    </div>
  );
};

const DepositMethods = ({onWidthChange}) => {
  const [showBankOptions, setShowBankOptions] = useState(false);
  const [showZelleOptions, setShowZelleOptions] = useState(false);
  const [showCardsOptions, setShowCardsOptions] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);
  const [showBinanceOptions, setShowBinanceOptions] = useState(false);
  const [showPaykuOptions, setShowPaykuOptions] = useState(false);

  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);
  const {triggerRegistrationModalIfNeeded} = usePagoMovilRegistration();
  const selectedCurrency = useCurrencyStore(state => state.selectedCurrency);

  const {t} = useTranslation();

  // Set modal width dynamically based on currency (larger for CLP to fit 4 options)
  useEffect(() => {
    if (onWidthChange) {
      // Check if we need to show Payku (CLP currency)
      const isShowingPayku = selectedCurrency?.id === "CLP";
      const width = isShowingPayku ? "950px" : "980px";
      onWidthChange(width);
    }
  }, [onWidthChange, selectedCurrency]);

  const prev = () => {
    setShowBankOptions(false);
    setShowZelleOptions(false);
    setShowBackButton(false);
    setShowCardsOptions(false);
    setShowBinanceOptions(false);
    setShowPaykuOptions(false);
    if (onWidthChange) {
      // Restore width based on currency
      const isShowingPayku = selectedCurrency?.id === "CLP";
      const width = isShowingPayku ? "950px" : "980px";
      onWidthChange(width);
    }
  };

  const handleBankClick = async () => {
    // Check if user needs to register cedula and phone number
    // Use forceRefresh to ensure we have the latest user data
    const modalTriggered = await triggerRegistrationModalIfNeeded("deposit", true);

    // Only proceed if registration is not needed
    if (!modalTriggered) {
      setShowBankOptions(true);
      setShowZelleOptions(false);
      setShowBackButton(true);
      if (onWidthChange) {
        onWidthChange("893px");
      }
    }
  };

  const handleZelleClick = () => {
    setShowZelleOptions(true);
    setShowBankOptions(false);
    setShowBackButton(true);
    if (onWidthChange) {
      onWidthChange("678px");
    }
  };

  const handleBinanceClick = () => {
    setShowBinanceOptions(true);
    setShowBankOptions(false);
    setShowZelleOptions(false);
    setShowPaykuOptions(false);
    setShowBackButton(true);
    if (onWidthChange) {
      onWidthChange("678px");
    }
  };

  const handlePaykuClick = () => {
    setShowPaykuOptions(true);
    setShowBankOptions(false);
    setShowZelleOptions(false);
    setShowBinanceOptions(false);
    setShowBackButton(true);
    if (onWidthChange) {
      onWidthChange("678px");
    }
  };

  return (
    <div className={containerCls}>
      {!showBankOptions && !showZelleOptions && !showCardsOptions && !showBinanceOptions && !showPaykuOptions && (
        <>
          <h4 className={instructionsCls}>{t("depositInstructionsOptions")}</h4>

          <div className={logoContainerCls}>
            {selectedCurrency?.id !== "CLP" && (
              <PagoMovilCard onClick={handleBankClick} />
            )}
            <MethodCard src={imgZelle} alt="Zelle" onClick={handleZelleClick} />
            <MethodCard src={imgBinance} alt="Binance" onClick={handleBinanceClick} />
            {selectedCurrency?.id === "CLP" && (
              <MethodCard src={imgPayku} alt="Payku" onClick={handlePaykuClick} />
            )}
          </div>
        </>
      )}

      {showBankOptions && <BankOptions prev={prev} />}
      {showZelleOptions && <ZelleOptions prev={prev} />}
      {showBinanceOptions && <BinanceOptions prev={prev} />}
      {showPaykuOptions && <PaykuOptions onBack={prev} />}

      {!showBackButton && (
        <div style={{display: "flex", justifyContent: "center", marginTop: "24px"}}>
          <button
            type="button"
            onClick={handleBankAccountModal}
            style={{
              height: 38,
              padding: "0 32px",
              borderRadius: 999,
              border: "1.5px solid rgba(215,50,168,0.5)",
              background: "transparent",
              color: "rgba(215,50,168,0.9)",
              fontWeight: 600,
              fontSize: "0.875rem",
              cursor: "pointer",
              transition: "border-color 0.2s, background 0.2s, color 0.2s",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = "linear-gradient(135deg,#fa5400,#d732a8)";
              e.currentTarget.style.borderColor = "transparent";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "rgba(215,50,168,0.5)";
              e.currentTarget.style.color = "rgba(215,50,168,0.9)";
            }}
          >
            {t("depositInstructionsCancelBtn")}
          </button>
        </div>
      )}
    </div>
  );
};
DepositMethods.propTypes = {
  onWidthChange: PropTypes.func,
};

export default DepositMethods;
